import {
    CreateReviewRequest,
    Review,
    ReviewListParams,
    ReviewListResponse,
    ScanResponse,
    Wine,
    WineListParams,
    WineListResponse,
} from '@/types'
import { apiService } from './api'

export class WineService {
    private static instance: WineService

    private constructor() {}

    public static getInstance(): WineService {
        if (!WineService.instance) {
            WineService.instance = new WineService()
        }
        return WineService.instance
    }

    // Wine methods
    async getWines(
        params?: WineListParams
    ): Promise<{ success: boolean; data?: WineListResponse; error?: string }> {
        try {
            const response = await apiService.getWines(params)

            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to fetch wines',
                }
            }
        } catch (error: unknown) {
            console.error('Get wines error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to fetch wines',
            }
        }
    }

    async getWine(id: number): Promise<{ success: boolean; data?: Wine; error?: string }> {
        try {
            const response = await apiService.getWine(id)

            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to fetch wine',
                }
            }
        } catch (error: unknown) {
            console.error('Get wine error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to fetch wine',
            }
        }
    }

    async getWineReviews(
        id: number,
        params?: ReviewListParams
    ): Promise<{ success: boolean; data?: ReviewListResponse; error?: string }> {
        try {
            const response = await apiService.getWineReviews(id, params)

            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to fetch wine reviews',
                }
            }
        } catch (error: unknown) {
            console.error('Get wine reviews error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to fetch wine reviews',
            }
        }
    }

    // Review methods
    async getReviews(
        params?: ReviewListParams
    ): Promise<{ success: boolean; data?: ReviewListResponse; error?: string }> {
        try {
            const response = await apiService.getReviews(params)

            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to fetch reviews',
                }
            }
        } catch (error: unknown) {
            console.error('Get reviews error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to fetch reviews',
            }
        }
    }

    async getReview(id: number): Promise<{ success: boolean; data?: Review; error?: string }> {
        try {
            const response = await apiService.getReview(id)

            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to fetch review',
                }
            }
        } catch (error: unknown) {
            console.error('Get review error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to fetch review',
            }
        }
    }

    async createReview(
        data: CreateReviewRequest
    ): Promise<{ success: boolean; data?: Review; error?: string }> {
        try {
            const response = await apiService.createReview(data)

            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to create review',
                }
            }
        } catch (error: unknown) {
            console.error('Create review error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to create review',
            }
        }
    }

    async updateReview(
        id: number,
        data: Partial<CreateReviewRequest>
    ): Promise<{ success: boolean; data?: Review; error?: string }> {
        try {
            const response = await apiService.updateReview(id, data)

            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to update review',
                }
            }
        } catch (error: unknown) {
            console.error('Update review error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to update review',
            }
        }
    }

    async deleteReview(id: number): Promise<{ success: boolean; error?: string }> {
        try {
            const response = await apiService.deleteReview(id)

            if (response.success) {
                return { success: true }
            } else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to delete review',
                }
            }
        } catch (error: unknown) {
            console.error('Delete review error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to delete review',
            }
        }
    }

    // Scan methods
    async scanWine(
        image: File
    ): Promise<{ success: boolean; data?: ScanResponse; error?: string }> {
        try {
            const response = await apiService.scanWine(image)

            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                return {
                    success: false,
                    error: response.error?.message || 'Failed to scan wine',
                }
            }
        } catch (error: unknown) {
            console.error('Scan wine error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to scan wine',
            }
        }
    }

    // Utility methods
    formatWineName(wine: Wine): string {
        let name = wine.name
        if (wine.year) {
            name += ` (${wine.year})`
        }
        if (wine.winemaker) {
            name += ` - ${wine.winemaker.name}`
        }
        return name
    }

    formatServingTemperature(wine: Wine): string {
        if (wine.serving_temp_min && wine.serving_temp_max) {
            return `${wine.serving_temp_min}°C - ${wine.serving_temp_max}°C`
        } else if (wine.serving_temp_min) {
            return `от ${wine.serving_temp_min}°C`
        } else if (wine.serving_temp_max) {
            return `до ${wine.serving_temp_max}°C`
        }
        return ''
    }

    getRatingStars(rating: number): string {
        return '★'.repeat(rating) + '☆'.repeat(5 - rating)
    }

    formatRating(rating: number): string {
        return `${rating}/5`
    }

    getWineImageUrl(wine: Wine): string {
        if (!wine.bg_image) return '/images/wine-placeholder.jpg'

        // In a real app, you might have different image sizes
        return wine.bg_image.url
    }

    getWinemakerImageUrl(winemaker: {
        logo_image?: { url: string } | null
        main_image?: { url: string } | null
    }): string {
        if (!winemaker.logo_image && !winemaker.main_image)
            return '/images/winemaker-placeholder.jpg'

        const image = winemaker.logo_image || winemaker.main_image
        return image?.url || '/images/winemaker-placeholder.jpg'
    }
}

export const wineService = WineService.getInstance()
